import { useDispatch, useSelector } from "react-redux";

import { IgButton, IgDialog, IgTypography } from "@intelligems/ig-theme";
import { GenericExperienceFe } from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { useFileBuild } from "@src/features/experiments/hooks/use-file-build";
import { isDialogOpen, setDialog } from "@src/store/slices/global";
import { multiCurrencyTesting } from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";

interface MarketPriceUpdaterStartDialogProps {
  currExperience: GenericExperienceFe;
}

export const MarketPriceUpdaterStartDialog = ({
  currExperience,
}: MarketPriceUpdaterStartDialogProps) => {
  const dispatch = useDispatch();
  const isOpen = useSelector(isDialogOpen("marketPriceUpdaterStart"));
  const { buildPriceCsvFile, downloadTemplateReady } = useFileBuild({
    experience: currExperience,
  });

  const handleClose = () => {
    dispatch(
      setDialog({
        dialog: "marketPriceUpdaterStart",
        open: false,
      })
    );
  };

  const continueAction = () => {
    dispatch(
      setDialog({
        dialog: "marketUpdateConfirm",
        open: true,
        props: {
          action: "start",
        },
      })
    );
    handleClose();
  };

  const onDownloadClick = () => {
    buildPriceCsvFile.mutate({
      body: {
        variations: [],
        selectedProducts: [],
        name: currExperience.name,
        experienceId: currExperience.id!,
        forExternalPriceUpload: true,
      },
      pathParams: {
        type: "csv",
      },
      queryParams: undefined,
    });
  };

  return isOpen ? (
    <IgDialog
      title={"Upload Prices to Your Market & Start Test"}
      rawContent={
        <Grid container direction={"column"} rowSpacing={2}>
          <Grid item>
            <IgTypography>
              In order for your test prices to adjust correctly, you'll need to
              set prices to the highest priced test group. Download the CSV and
              upload your prices to Shopify Markets or Global-e before starting
              your test.
              <IgTypography
                igStyle={"link"}
                sx={{ fontWeight: 400 }}
                onClick={() => {
                  openNewTab(multiCurrencyTesting);
                }}
              >
                {" "}
                Find out more.
              </IgTypography>
            </IgTypography>
          </Grid>
          <Grid item container direction={"column"}>
            <Grid item>
              <IgButton
                variant={"contained"}
                igVariant={
                  downloadTemplateReady.isReady ? "success" : undefined
                }
                loading={buildPriceCsvFile.isPending}
                onClick={() => {
                  if (
                    downloadTemplateReady.isReady &&
                    downloadTemplateReady.href
                  ) {
                    window.open(downloadTemplateReady.href, "_blank");
                  } else {
                    onDownloadClick();
                  }
                }}
              >
                {downloadTemplateReady.isReady
                  ? "CSV Ready - Click to Download"
                  : "Download CSV"}
              </IgButton>
            </Grid>
            <Grid item>
              <IgButton onClick={continueAction}>
                {"I've downloaded the CSV, continue"}
              </IgButton>
            </Grid>
          </Grid>
        </Grid>
      }
      handleClose={handleClose}
      hideActions={true}
      showCloseIcon={true}
      showDialog={isOpen}
      dialogHeight={null}
      maxWidth={"xs"}
    />
  ) : null;
};
